import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, flow, first, cloneDeep } from 'lodash';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import { Matcher } from 'utils/adl-services';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
} from 'containers/Survey/constants';

import {
  getEmptyAnswer as getEmptyScreeningAnswer,
  getEmptyNoneOfTheAboveAnswer,
} from 'components/ScreeningQuestion/utils';
import {
  getEmptyAnswer,
  otherSelector,
  noneOfTheAboveAnswerSelector,
} from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
} from '../utils';

const cpgPathToPurchaseGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const brands = get(find(variables, ['name', 'brands']), 'value', []);
  const storeBrand = get(find(variables, ['name', 'store_brand']), 'value', {});
  const otherProductCategories = get(
    find(variables, ['name', 'other_product_categories']),
    'value',
    [],
  );

  const period = get(find(variables, ['name', 'period']), 'value', '');
  const includeFast = get(
    find(variables, ['name', 'include_fast']),
    'value',
    false,
  );

  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  // replace general texts
  const replacePeriod = (value) =>
    value.replace(/\{\{period\}\}/gi, escapeHTML(period));
  const replaceProductCategory = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );
  const replaceStoreBrand = (value) =>
    value.replace(/\{\{store_brand\}\}/gi, escapeHTML(storeBrand.text));

  const replaceAllTexts = flow([
    replaceStoreBrand,
    replaceProductCategory,
    replacePeriod,
  ]);

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceAllTexts,
      htmlPrompt: replaceAllTexts,
      body: replaceAllTexts,
    },
  );

  const { nodes: cpgPathToPurchaseTemplateQuestions, screeningQuestions } =
    mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  const screeningQuestionClone = cloneDeep(screeningQuestions[0]);
  Object.assign(screeningQuestionClone, {
    answers: [
      {
        ...getEmptyScreeningAnswer(),
        body: productCategory.text,
        media: productCategory.media,
      },
      ...otherProductCategories.map((otherCategory) => ({
        ...getEmptyScreeningAnswer(),
        body: otherCategory.text,
        media: otherCategory.media,
        screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
      })),
      {
        ...getEmptyNoneOfTheAboveAnswer(),
        screenerType: SCREENING_ALLOWED_VALUES.STRICT_NOT_ALLOWED,
      },
    ],
  });

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  // setup media for questions
  [0, 1, 2, 3].forEach((qIndex) => {
    Survey.questionnaire.updateQuestion({
      ...cpgPathToPurchaseTemplateQuestions[qIndex],
      media: productCategory.media,
    });
  });

  Survey.questionnaire.updateQuestion({
    ...cpgPathToPurchaseTemplateQuestions[9],
    answers: [
      {
        ...first(cpgPathToPurchaseTemplateQuestions[9].answers),
        media: productCategory.media,
      },
      ...otherProductCategories.map((otherCategory) => ({
        ...getEmptyAnswer(true),
        body: otherCategory.text,
        media: otherCategory.media,
      })),
      otherSelector(cpgPathToPurchaseTemplateQuestions[9].answers),
      noneOfTheAboveAnswerSelector(
        cpgPathToPurchaseTemplateQuestions[9].answers,
      ),
    ],
  });

  Survey.questionnaire.updateQuestion({
    ...cpgPathToPurchaseTemplateQuestions[11],
    answers: [
      ...otherProductCategories.map((otherCategory) => ({
        ...getEmptyAnswer(true),
        body: otherCategory.text,
        media: otherCategory.media,
      })),
      otherSelector(cpgPathToPurchaseTemplateQuestions[11].answers),
      noneOfTheAboveAnswerSelector(
        cpgPathToPurchaseTemplateQuestions[11].answers,
      ),
    ],
  });

  Survey.questionnaire.updateQuestion({
    ...cpgPathToPurchaseTemplateQuestions[13],
    answers: [
      ...brands.map((brand) => ({
        ...getEmptyAnswer(true),
        body: brand.text,
        media: brand.media,
      })),
      {
        ...getEmptyAnswer(true),
        body: storeBrand.text,
        media: storeBrand.media,
      },
      {
        ...getEmptyAnswer(true),
        body: 'Other Brand',
      },
    ],
  });

  Survey.targeting.updateScreeningQuestion(screeningQuestionClone);
  applyScreeningQuestionContentUpdate(Survey, screeningQuestionClone, {
    surveyType,
    peeqCategoryType,
  });

  if (includeFast) {
    Survey.questionnaire.removeQuestion({
      refId: cpgPathToPurchaseTemplateQuestions[1].refId,
    });
  } else {
    Survey.questionnaire.removeQuestion({
      refId: cpgPathToPurchaseTemplateQuestions[0].refId,
    });
  }

  if (
    shouldConvertScreeningToQlQuestions({
      surveyType,
      peeqCategoryType,
    })
  ) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screenerQuestion = screeningQuestionToNormal(screeningQuestionClone);
    Survey.questionnaire.addQuestion(screenerQuestion);

    repointCarryForwardFromScreening(
      Survey,
      screenerQuestion.refId,
      screeningQuestionClone.refId,
    );

    // move to top of questionnaire
    Survey.questionnaire.moveQuestion(
      screenerQuestion,
      // eslint-disable-next-line no-underscore-dangle
      -(Survey.questionnaire._getQuestionCount() - 1),
    );

    Survey.questionnaire.updateQuestion(screenerQuestion);

    Survey.questionnaire.removeAllScreeningQuestions();

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: screenerQuestion,
      answerRefId: first(screenerQuestion.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: screenerQuestion.refId },
      matcher: Matcher.NotEqual,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: screenerQuestion.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });
  } else if (
    shouldUseScreeningQl({
      surveyType,
      peeqCategoryType,
    })
  ) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }

  return { state: Survey.state, defaultRecurrence };
};

export default cpgPathToPurchaseGenerator;
